import { useEffect } from 'react';
import type { CartLine } from './cartCount';

/**
 * The header mini-cart's cart read, shared with the page (CPS-7696). The
 * island reads the cart on every MULTI_CART page for its badge, so a PDP
 * under it reads those lines rather than requesting the same cart again.
 */

type SharedCartLines = {
  /** Whose cart the lines are; see `cartLinesOwner`. */
  owner: string;
  /** `undefined` while the header's read is pending or failed. */
  lines: CartLine[] | undefined;
};

let isHosted = false;
let shared: SharedCartLines | undefined;
const listeners = new Set<() => void>();
const notify = () => listeners.forEach((listener) => listener());

/** A user's cart in one org, or a guest cart; `undefined` until it is known. */
export const cartLinesOwner = ({
  userId,
  organizationId,
  guestCartId,
}: {
  userId?: string;
  organizationId?: string;
  guestCartId?: string;
}): string | undefined => {
  if (userId) {
    return organizationId ? `user:${userId}:${organizationId}` : undefined;
  }
  return guestCartId ? `guest:${guestCartId}` : undefined;
};

export const subscribeToSharedCartLines = (
  listener: () => void
): (() => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

/** Whether the header mini-cart reads the cart for this page. */
export const isCartReadHosted = (): boolean => isHosted;

export const readSharedCartLines = (): SharedCartLines | undefined => shared;

/** The header's latest read; no owner clears it. */
export const publishCartLines = (
  owner: string | undefined,
  lines: CartLine[] | undefined
): void => {
  if (shared?.owner === owner && shared?.lines === lines) return;
  shared = owner ? { owner, lines } : undefined;
  notify();
};

/**
 * Called by the header mini-cart's host, so page readers leave the cart read
 * to it. Marked during render, not only from an effect: the PDP renders after
 * the header and would otherwise send its own read before the header's
 * effects run. Idempotent, so a repeated render costs nothing.
 */
export const useHostCartRead = (): void => {
  if (typeof window !== 'undefined') isHosted = true;
  useEffect(() => {
    isHosted = true;
    notify();
    return releaseCartRead;
  }, []);
};

const releaseCartRead = () => {
  isHosted = false;
  shared = undefined;
  notify();
};

/**
 * Hands the cart read back to the page once the header mini-cart failed: a
 * host that threw before mounting marked the read during render but never
 * unmounts to release it.
 */
export const useReleaseCartRead = (): void => {
  useEffect(releaseCartRead, []);
};
